import { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo, type ReactNode } from 'react';
import { startOfDay, endOfDay, format, parseISO } from 'date-fns';
import { useBroadcastChannel, type BroadcastMessage } from '@/hooks/useBroadcastChannel';

interface DateRange {
  start: Date;
  end: Date;
}

interface DateRangeContextType {
  dateRange: DateRange;
  setDateRange: (range: DateRange, preset?: string) => void;
  resetToToday: () => void;
  // Formato para queries
  formattedRange: {
    startDate: string;
    endDate: string;
  };
  // Período confirmado — só muda quando "Aplicar" é clicado
  committedDateRange: DateRange;
  committedDateRangeFromTo: { from: Date; to: Date };
  committedFormattedRange: {
    startDate: string;
    endDate: string;
  };
  hasPendingDateChange: boolean;
  applyDateRange: () => void;
}

// Chave do localStorage para sincronizar date range entre abas
const DATE_RANGE_STORAGE_KEY = 'd12-date-range';

const DateRangeContext = createContext<DateRangeContextType | undefined>(undefined);

/**
 * Salva o date range no localStorage
 */
function saveDateRangeToStorage(startDate: string, endDate: string, preset?: string) {
  try {
    localStorage.setItem(DATE_RANGE_STORAGE_KEY, JSON.stringify({ startDate, endDate, preset }));
  } catch {
    // Ignorar erros de localStorage
  }
}

/**
 * Carrega o date range do localStorage
 * Se o preset for "today", atualiza automaticamente para a data de hoje
 */
function loadDateRangeFromStorage(): { range: DateRange; preset?: string } | null {
  try {
    const stored = localStorage.getItem(DATE_RANGE_STORAGE_KEY);
    if (stored) {
      const { startDate, endDate, preset } = JSON.parse(stored);

      // Se o preset era "today", verificar se precisa atualizar para o dia atual
      if (preset === 'today') {
        const today = format(new Date(), 'yyyy-MM-dd');
        if (startDate !== today || endDate !== today) {
          // Data mudou, atualizar para hoje
          const todayDate = new Date();
          // Atualizar localStorage com a nova data
          saveDateRangeToStorage(today, today, 'today');
          return {
            range: { start: startOfDay(todayDate), end: endOfDay(todayDate) },
            preset: 'today'
          };
        }
      }

      return {
        range: {
          start: startOfDay(parseISO(startDate)),
          end: endOfDay(parseISO(endDate)),
        },
        preset
      };
    }
  } catch {
    // Ignorar erros de parse
  }
  return null;
}

export function DateRangeProvider({ children }: { children: ReactNode }) {
  const isHandlingCrossTabEventRef = useRef(false);

  // Handler para mensagens de outras abas — só atualiza o picker (UI)
  const handleCrossTabMessage = useCallback((message: BroadcastMessage) => {
    if (message.type !== 'DATE_RANGE_CHANGED') return;

    // Evitar loops
    if (isHandlingCrossTabEventRef.current) return;
    isHandlingCrossTabEventRef.current = true;

    try {
      const payload = message.payload as { startDate: string; endDate: string; preset?: string } | undefined;
      if (payload?.startDate && payload?.endDate) {
        // Atualizar apenas o picker (UI) — usuário precisa clicar "Aplicar" para buscar dados
        setDateRangeState({
          start: startOfDay(parseISO(payload.startDate)),
          end: endOfDay(parseISO(payload.endDate)),
        });
        currentPresetRef.current = payload.preset;
      }
    } finally {
      setTimeout(() => {
        isHandlingCrossTabEventRef.current = false;
      }, 100);
    }
  }, []);

  // Inicializar BroadcastChannel
  const { postMessage } = useBroadcastChannel(handleCrossTabMessage);

  // Ref para armazenar o preset atual (não precisa re-renderizar quando muda)
  const currentPresetRef = useRef<string | undefined>(undefined);

  // Inicializar com valor do localStorage ou dia de hoje
  const [dateRange, setDateRangeState] = useState<DateRange>(() => {
    // Tentar carregar do localStorage primeiro
    const storedData = loadDateRangeFromStorage();
    if (storedData) {
      currentPresetRef.current = storedData.preset;
      return storedData.range;
    }

    // Se não houver, usar dia de hoje (sem preset definido)
    const today = new Date();
    return {
      start: startOfDay(today),
      end: endOfDay(today)
    };
  });

  // Atualiza apenas o picker (UI) — NÃO dispara fetch.
  // Dados só são buscados quando o usuário clica "Aplicar" (applyDateRange).
  const setDateRange = useCallback((range: DateRange, preset?: string) => {
    // Se está processando evento de outra aba, não fazer broadcast
    if (isHandlingCrossTabEventRef.current) {
      setDateRangeState({
        start: startOfDay(range.start),
        end: endOfDay(range.end)
      });
      currentPresetRef.current = preset;
      return;
    }

    const newStart = startOfDay(range.start);
    const newEnd = endOfDay(range.end);
    const startDate = format(newStart, 'yyyy-MM-dd');
    const endDate = format(newEnd, 'yyyy-MM-dd');

    // Atualizar state e preset (somente UI) — imediato, sem debounce
    setDateRangeState({
      start: newStart,
      end: newEnd
    });
    currentPresetRef.current = preset;

    // Salvar no localStorage para sincronização (inclui preset)
    saveDateRangeToStorage(startDate, endDate, preset);

    // Notificar outras abas (inclui preset)
    postMessage('DATE_RANGE_CHANGED', { startDate, endDate, preset });
  }, [postMessage]);

  // Função para resetar para hoje
  const resetToToday = useCallback(() => {
    const today = new Date();
    const start = startOfDay(today);
    const end = endOfDay(today);
    const startDate = format(start, 'yyyy-MM-dd');
    const endDate = format(end, 'yyyy-MM-dd');

    setDateRangeState({ start, end });
    currentPresetRef.current = 'today';

    // Salvar e notificar outras abas (com preset 'today')
    saveDateRangeToStorage(startDate, endDate, 'today');
    postMessage('DATE_RANGE_CHANGED', { startDate, endDate, preset: 'today' });
  }, [postMessage]);

  // Escutar mudanças no localStorage (fallback para navegadores sem BroadcastChannel)
  // Só atualiza o picker (UI) — usuário precisa clicar "Aplicar" para buscar dados
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== DATE_RANGE_STORAGE_KEY || !event.newValue) return;

      // Evitar loops
      if (isHandlingCrossTabEventRef.current) return;
      isHandlingCrossTabEventRef.current = true;

      try {
        const { startDate, endDate, preset } = JSON.parse(event.newValue);

        setDateRangeState({
          start: startOfDay(parseISO(startDate)),
          end: endOfDay(parseISO(endDate)),
        });
        currentPresetRef.current = preset;
      } catch {
        // Ignorar erros de parse
      } finally {
        setTimeout(() => {
          isHandlingCrossTabEventRef.current = false;
        }, 100);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Formatar para uso em queries
  const formattedRange = {
    startDate: format(dateRange.start, 'yyyy-MM-dd'),
    endDate: format(dateRange.end, 'yyyy-MM-dd')
  };

  // Período confirmado — persiste entre navegações, só muda via "Aplicar"
  const [committedDateRange, setCommittedDateRange] = useState<DateRange>(() => ({
    start: dateRange.start,
    end: dateRange.end,
  }));

  const committedFormattedRange = useMemo(() => ({
    startDate: format(committedDateRange.start, 'yyyy-MM-dd'),
    endDate: format(committedDateRange.end, 'yyyy-MM-dd'),
  }), [committedDateRange.start, committedDateRange.end]);

  // Alias {from, to} para hooks que esperam esse formato
  const committedDateRangeFromTo = useMemo(() => ({
    from: committedDateRange.start,
    to: committedDateRange.end,
  }), [committedDateRange.start, committedDateRange.end]);

  const hasPendingDateChange =
    format(dateRange.start, 'yyyy-MM-dd') !== format(committedDateRange.start, 'yyyy-MM-dd') ||
    format(dateRange.end, 'yyyy-MM-dd') !== format(committedDateRange.end, 'yyyy-MM-dd');

  const applyDateRange = useCallback(() => {
    setCommittedDateRange({ start: dateRange.start, end: dateRange.end });
  }, [dateRange.start, dateRange.end]);

  return (
    <DateRangeContext.Provider
      value={{
        dateRange,
        setDateRange,
        resetToToday,
        formattedRange,
        committedDateRange,
        committedDateRangeFromTo,
        committedFormattedRange,
        hasPendingDateChange,
        applyDateRange,
      }}
    >
      {children}
    </DateRangeContext.Provider>
  );
}

export function useDateRange() {
  const context = useContext(DateRangeContext);
  if (!context) {
    throw new Error('useDateRange must be used within a DateRangeProvider');
  }
  return context;
}
